import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { generateClient } from 'aws-amplify/api';
import { FiHeart, FiMessageSquare, FiEye, FiBookmark, FiShare2, FiLink, FiCopy, FiCheck } from 'react-icons/fi';
import { downloadData, getUrl } from 'aws-amplify/storage';
import { PageContainer } from '../App';
import { createShortUrl } from '../utils/urlShortener';
import { FaXTwitter, FaFacebook, FaLinkedin, FaWhatsapp } from 'react-icons/fa6';
import { BiMessageDetail } from 'react-icons/bi';
import MetaTags from '../components/MetaTags';

const client = generateClient();

// Add getS3ImageUrl helper function
const getS3ImageUrl = async (key) => {
  if (!key) return null;
  try {
    const { url } = await getUrl({ path: key });
    return url;
  } catch (err) {
    console.error('Error getting image URL:', err);
    return null;
  }
};

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [content, setContent] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [copyUrl, setCopyUrl] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const { data } = await client.models.Article.get({ id });
        if (!data) {
          setError('Article not found');
          return;
        }

        // Get signed URL for banner image
        const fullImageUrl = await getS3ImageUrl(data.bannerImageUrl);
        
        // Set article with processed image URL
        setArticle({
          ...data,
          bannerImageUrl: fullImageUrl
        });

        // Download article content from S3
        if (data.contentS3Url) {
          const downloadResult = await downloadData({
            path: data.contentS3Url
          }).result;
          const rawContent = await downloadResult.body.text();

          // Create a temporary div to parse and modify the content
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = rawContent;

          // Find all images with s3key and replace with signed URLs
          const images = tempDiv.getElementsByTagName('img');
          await Promise.all(Array.from(images).map(async (img) => {
            const s3key = img.dataset.s3key;
            if (s3key) {
              try {
                const { url } = await getUrl({ path: s3key });
                img.src = url;
              } catch (err) {
                console.error('Error getting signed URL for image:', err);
                // Keep the original src if there's an error
              }
            }
          }));

          // Set the processed content
          setContent(tempDiv.innerHTML);
        }

        // Check if article is bookmarked
        const bookmarkIds = JSON.parse(localStorage.getItem('bookmarkIds') || '[]');
        setIsBookmarked(bookmarkIds.includes(id));

        // Update view count
        await client.models.Article.update({
          id,
          views: (data.views || 0) + 1
        });

        // Fetch comments
        const comments = await client.models.Comment.list({
          filter: { articleId: { eq: id } }
        });

        setComments(comments.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBookmark = () => {
    const bookmarkIds = JSON.parse(localStorage.getItem('bookmarkIds') || '[]');
    
    if (isBookmarked) {
      // Remove bookmark
      const updatedBookmarkIds = bookmarkIds.filter(id => id !== article.id);
      localStorage.setItem('bookmarkIds', JSON.stringify(updatedBookmarkIds));
      setIsBookmarked(false);
    } else {
      // Add bookmark
      const updatedBookmarkIds = [...bookmarkIds, article.id];
      localStorage.setItem('bookmarkIds', JSON.stringify(updatedBookmarkIds));
      setIsBookmarked(true);
    }
  };

  const handleLike = async () => {
    try {
      const { data: updatedArticle } = await client.models.Article.update({
        id,
        likes: (article.likes || 0) + 1
      });
      setArticle(updatedArticle);
    } catch (err) {
      console.error('Error liking article:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      // Create the comment
      const { data: newCommentData } = await client.models.Comment.create({
        content: newComment,
        articleId: id,
        author: "Anonymous",
        createdAt: new Date().toISOString()
      });

      // Update article's comment count
      const { data: updatedArticle } = await client.models.Article.update({
        id,
        commentsCount: (article.commentsCount || 0) + 1
      });

      setComments([...comments, newCommentData]);
      setArticle(updatedArticle);
      setNewComment('');
    } catch (err) {
      console.error('Error posting comment:', err);
    }
  };

  const handleCommentLike = async (commentId) => {
    try {
      const { data: updatedComment } = await client.models.Comment.update({
        id: commentId,
        likes: (comments.find(c => c.id === commentId)?.likes || 0) + 1
      });

      setComments(comments.map(comment => 
        comment.id === commentId ? updatedComment : comment
      ));
    } catch (err) {
      console.error('Error liking comment:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      const shortCode = await createShortUrl(article.id);
      const shortUrl = `${window.location.origin}/s/${shortCode}`;
      
      try {
        await navigator.clipboard.writeText(shortUrl);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 2000);
      } catch (err) {
        setCopyUrl(shortUrl);
        setShowCopyPopup(true);
        setTimeout(() => setShowCopyPopup(false), 5000);
      }
      
      setShowShareMenu(false);
    } catch (error) {
      console.error('Error generating link:', error);
      setCopyUrl('Failed to generate link. Please try again.');
      setShowCopyPopup(true);
      setTimeout(() => setShowCopyPopup(false), 3000);
    }
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(article.title);
    let shareUrl;

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
      case 'sms':
        shareUrl = `sms:?&body=${title}%20${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      default:
        return;
    }

    // For SMS, we'll open in the same window since it's a protocol handler
    if (platform === 'sms') {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return <LoadingMessage>Loading article...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  if (!article) {
    return <ErrorMessage>Article not found</ErrorMessage>;
  }

  return (
    <PageContainer>
      {article && (
        <MetaTags
          title={article.title}
          description={article.content.substring(0, 200) + '...'}
          image={article.bannerImageUrl}
          url={`${window.location.origin}/article/${article.id}`}
          type="article"
          keywords={['parenting', 'family', article.title.toLowerCase()]}
          author={article.author}
          publishedTime={article.createdAt}
          modifiedTime={article.updatedAt}
          canonicalUrl={`${window.location.origin}/article/${article.id}`}
        >
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "image": article.bannerImageUrl,
              "author": {
                "@type": "Person",
                "name": article.author
              },
              "publisher": {
                "@type": "Organization",
                "name": "Parenting Beyond Perfection",
                "logo": {
                  "@type": "ImageObject",
                  "url": `${window.location.origin}/logo.png`
                }
              },
              "datePublished": article.createdAt,
              "dateModified": article.updatedAt,
              "description": article.content.substring(0, 200).trim() + '...',
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${window.location.origin}/article/${article.id}`
              }
            })}
          </script>
        </MetaTags>
      )}
      <ArticleContent>
        <Article>
          <ArticleHeader>
            <Title>{article.title}</Title>
            <AuthorSection>
              <AuthorImage />
              <AuthorInfo>
                <AuthorContent>
                  <AuthorName>{article.author}</AuthorName>
                  <ArticleMeta>
                    <span>{article.readTime} min read</span>
                    <span>· {new Date(article.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}</span>
                  </ArticleMeta>
                </AuthorContent>
              </AuthorInfo>
            </AuthorSection>
            <MetricsRow>
              <MetricButton onClick={handleLike}>
                <FiHeart /> {article.likes || 0}
              </MetricButton>
              <MetricItem>
                <FiMessageSquare /> {article.commentsCount || 0}
              </MetricItem>
              <MetricItem>
                <FiEye /> {article.views || 0}
              </MetricItem>
              <ShareMenuContainer>
                <ShareButton onClick={() => setShowShareMenu(!showShareMenu)}>
                  <FiShare2 /> Share
                </ShareButton>
                {showShareMenu && (
                  <ShareMenu ref={shareMenuRef}>
                    <ShareMenuItem onClick={handleCopyLink}>
                      <FiLink />
                      Copy link
                    </ShareMenuItem>
                    <ShareMenuItem onClick={() => handleShare('whatsapp')}>
                      <FaWhatsapp />
                      Share on WhatsApp
                    </ShareMenuItem>
                    <ShareMenuItem onClick={() => handleShare('sms')}>
                      <BiMessageDetail />
                      Send via Messages
                    </ShareMenuItem>
                    <ShareMenuItem onClick={() => handleShare('twitter')}>
                      <FaXTwitter />
                      Share on X
                    </ShareMenuItem>
                    <ShareMenuItem onClick={() => handleShare('facebook')}>
                      <FaFacebook />
                      Share on Facebook
                    </ShareMenuItem>
                    <ShareMenuItem onClick={() => handleShare('linkedin')}>
                      <FaLinkedin />
                      Share on LinkedIn
                    </ShareMenuItem>
                  </ShareMenu>
                )}
              </ShareMenuContainer>
              <BookmarkButton onClick={handleBookmark} isBookmarked={isBookmarked}>
                <FiBookmark />
              </BookmarkButton>
            </MetricsRow>
          </ArticleHeader>
          <Content dangerouslySetInnerHTML={{ __html: content }} />
        </Article>

        <CommentsSection>
          <CommentHeader>
            <h2>Comments ({comments.length})</h2>
          </CommentHeader>

          <CommentForm onSubmit={handleCommentSubmit}>
            <CommentInput
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
            />
            <CommentButton type="submit">Post Comment</CommentButton>
          </CommentForm>

          <CommentsList>
            {comments.map((comment) => (
              <CommentCard key={comment.id}>
                <CommentAuthor>
                  <AnonymousImage />
                  <div>
                    <AuthorName>{comment.author}</AuthorName>
                    <CommentDate>
                      {new Date(comment.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </CommentDate>
                  </div>
                </CommentAuthor>
                <CommentContent>{comment.content}</CommentContent>
                <CommentActions>
                  <ActionButton onClick={() => handleCommentLike(comment.id)}>
                    <FiHeart /> {comment.likes || 0}
                  </ActionButton>
                </CommentActions>
              </CommentCard>
            ))}
          </CommentsList>
        </CommentsSection>
      </ArticleContent>
      {showCopyPopup && (
        <CopyPopup>
          <span>Copy this link:</span>
          <CopyUrlInput value={copyUrl} readOnly />
          <CopyButton onClick={() => {
            navigator.clipboard.writeText(copyUrl)
              .then(() => {
                setShowCopyPopup(false);
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 2000);
              })
              .catch(() => {
                // Just keep the popup open if copy fails
              });
          }}>
            <FiCopy /> Copy
          </CopyButton>
        </CopyPopup>
      )}
      {showSuccessMessage && (
        <SuccessMessage>
          <FiCheck /> Link copied to clipboard
        </SuccessMessage>
      )}
    </PageContainer>
  );
};

const ArticleContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Article = styled.article`
  background: white;
  border-radius: 4px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ArticleHeader = styled.header`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 1.5rem;
  font-family: 'Source Serif 4', Georgia, serif;
  color: #292929;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin: 0 0 1rem;
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AuthorContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: url('/author.jpeg');
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AuthorName = styled.span`
  font-weight: 500;
  color: #292929;
  font-size: 1.2rem;
`;

const ArticleMeta = styled.div`
  color: #757575;
  font-size: 0.9rem;
  display: flex;
  gap: 0.5rem;
`;

const MetricsRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1rem;
  color: #757575;
  font-size: 1rem;
  border-top: 1px solid #e6e6e6;
  padding-top: 1rem;
`;

const MetricItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetricButton = styled(MetricItem)`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #e53935;
  }
`;

const BookmarkButton = styled(MetricButton)`
  color: ${props => props.isBookmarked ? '#1a8917' : '#757575'};
  padding: 0.5rem;
  margin: -0.5rem; /* Compensate for padding to maintain alignment */

  &:hover {
    color: ${props => props.isBookmarked ? '#156912' : '#292929'};
  }

  svg {
    fill: ${props => props.isBookmarked ? 'currentColor' : 'none'};
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  font-size: 1.2rem;
  line-height: 1.8;
  color: #292929;
  font-family: 'Source Serif 4', Georgia, serif;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.7;
  }

  p {
    margin-bottom: 2rem;
  }

  h2 {
    font-family: 'Source Serif 4', Georgia, serif;
    font-size: 1.8rem;
    margin: 2rem 0 1rem;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  a {
    color: #1a8917;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 2rem 0;
    border-radius: 4px;
  }

  blockquote {
    border-left: 3px solid #292929;
    margin: 2rem 0;
    padding-left: 1.5rem;
    font-style: italic;

    @media (max-width: 768px) {
      margin: 1.5rem 0;
      padding-left: 1rem;
    }
  }

  pre {
    background: #f5f5f5;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 2rem 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 8rem 2rem;
  color: #757575;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 8rem 2rem;
  color: #e53935;
  font-size: 1.2rem;
`;

const CommentsSection = styled.section`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e6e6e6;
  background: white;
  border-radius: 4px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 2rem;
  }
`;

const CommentHeader = styled.div`
  margin-bottom: 2rem;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
  }
`;

const CommentForm = styled.form`
  margin-bottom: 2rem;
`;

const CommentInput = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  font-size: 1rem;

  @media (max-width: 768px) {
    min-height: 80px;
    padding: 0.8rem;
  }

  &:focus {
    outline: none;
    border-color: #1a8917;
  }
`;

const CommentButton = styled.button`
  background: #1a8917;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 99px;
  font-size: 0.9rem;
  cursor: pointer;

  &:hover {
    background: #156912;
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const CommentCard = styled.div`
  padding: 1.5rem;
  border-radius: 4px;
  background: white;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CommentAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: flex-start;
`;

const AnonymousImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:before {
    content: '';
    width: 24px;
    height: 24px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23757575'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const CommentDate = styled.span`
  color: #757575;
  font-size: 0.9rem;
  display: block;
`;

const CommentContent = styled.p`
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #757575;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem;

  &:hover {
    color: #e53935;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ShareMenuContainer = styled.div`
  position: relative;
`;

const ShareButton = styled(MetricButton)`
  position: relative;
`;

const ShareMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 240px;
  z-index: 1000;
  overflow: hidden;
`;

const ShareMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: #292929;
  font-size: 1rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    width: 20px;
    height: 20px;
    color: #757575;
  }
`;

const CopyPopup = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1000;
  max-width: 90vw;
  border: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
`;

const CopyUrlInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  font-size: 0.9rem;
  width: 200px;
  color: #757575;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CopyButton = styled.button`
  background: #1a8917;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: #156912;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SuccessMessage = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a8917;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 99px;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1000;
`;

export default ArticlePage;